import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { CheckCircle2, AlertOctagon, AlertTriangle, Info } from 'lucide-react';

/**
 * FeedbackDialog - Generic outcome feedback modal for Success / Error / Warning / Info states.
 */
export function FeedbackDialog({
  open,
  onClose,
  type = 'info', // 'success' | 'error' | 'warning' | 'info'
  title,
  message,
  primaryActionText = 'Done',
  onPrimaryAction,
  secondaryActionText,
  onSecondaryAction,
  children,
  maxWidth = 'xs',
}) {
  const theme = useTheme();

  const configs = {
    success: {
      color: theme.palette.success.main,
      lighter: theme.palette.success.lighter || alpha(theme.palette.success.main, 0.12),
      icon: CheckCircle2,
      btnColor: 'success',
      defaultTitle: 'Success!',
    },
    error: {
      color: theme.palette.error.main,
      lighter: theme.palette.error.lighter || alpha(theme.palette.error.main, 0.12),
      icon: AlertOctagon,
      btnColor: 'error',
      defaultTitle: 'Something went wrong',
    },
    warning: {
      color: theme.palette.warning.main,
      lighter: theme.palette.warning.lighter || alpha(theme.palette.warning.main, 0.12),
      icon: AlertTriangle,
      btnColor: 'warning',
      defaultTitle: 'Attention needed',
    },
    info: {
      color: theme.palette.info.main,
      lighter: theme.palette.info.lighter || alpha(theme.palette.info.main, 0.12),
      icon: Info,
      btnColor: 'primary',
      defaultTitle: 'Notice',
    },
  };

  const current = configs[type] || configs.info;
  const IconComponent = current.icon;

  const handlePrimary = () => {
    if (onPrimaryAction) onPrimaryAction();
    else if (onClose) onClose();
  };

  const handleSecondary = () => {
    if (onSecondaryAction) onSecondaryAction();
    else if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            p: 0,
            textAlign: 'center',
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogContent sx={{ px: 3, pt: 4, pb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            bgcolor: current.lighter,
            color: current.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: `0 8px 16px -4px ${alpha(current.color, 0.25)}`,
          }}
        >
          <IconComponent size={28} strokeWidth={2.4} />
        </Box>

        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: '1.15rem',
            letterSpacing: '-0.015em',
            color: 'text.primary',
            mb: 0.75,
          }}
        >
          {title || current.defaultTitle}
        </Typography>

        {message && (
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, maxWidth: 360 }}>
            {message}
          </Typography>
        )}

        {children}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          pt: 1.5,
          justifyContent: 'center',
          gap: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.4) : alpha(theme.palette.grey[50], 0.8),
        }}
      >
        {secondaryActionText && (
          <Button variant="outlined" onClick={handleSecondary} sx={{ minWidth: 100, fontWeight: 600 }}>
            {secondaryActionText}
          </Button>
        )}
        <Button
          variant="contained"
          color={current.btnColor}
          onClick={handlePrimary}
          sx={{ minWidth: 110, fontWeight: 700 }}
        >
          {primaryActionText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const SuccessDialog = (props) => <FeedbackDialog {...props} type="success" primaryActionText="Done" />;
export const ErrorDialog = (props) => <FeedbackDialog {...props} type="error" primaryActionText="Try Again" />;
export const WarningDialog = (props) => <FeedbackDialog {...props} type="warning" primaryActionText="Continue" />;
export const InfoDialog = (props) => <FeedbackDialog {...props} type="info" primaryActionText="Got it" />;

export default FeedbackDialog;
